import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Loader2 } from "lucide-react";
import API from "@/lib/api";

/**
 * Library Health pill — compact score summary for Dashboard & Packs page headers.
 * Click to open Library.
 */
export function HealthScore({ testId = "health-score", variant = "pill" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    API.get("/library/health")
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-1.5 h-7 px-2 border border-[var(--pf-border)] text-[10px] text-[var(--pf-text-muted)]" data-testid={`${testId}-loading`}>
        <Loader2 className="w-3 h-3 animate-spin" />Health...
      </div>
    );
  }
  if (!data) return null;

  const color = data.score >= 70 ? "#16A34A" : data.score >= 40 ? "#D97706" : "#DC2626";
  const gradeBg = data.score >= 70 ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
    : data.score >= 40 ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400"
    : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400";

  return (
    <div className="relative" data-testid={testId}>
      <button
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        className="flex items-center gap-1.5 h-7 px-2 border border-[var(--pf-border)] hover:border-[var(--pf-accent)] transition-colors"
        title={`Library Health Score: ${data.score}/100 (${data.grade})`}
        data-testid={`${testId}-button`}
      >
        <Heart className="w-3 h-3" style={{ color }} />
        <span className="text-[11px] font-mono font-semibold text-[var(--pf-text)]">{data.score}</span>
        <span className={`text-[9px] px-1 py-0 font-bold uppercase tracking-wider ${gradeBg}`}>{data.grade}</span>
        <span className="hidden md:inline text-[10px] text-[var(--pf-text-muted)]">library health</span>
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-50 w-[320px] border border-[var(--pf-border)] bg-[var(--pf-bg)] shadow-lg p-3 space-y-3"
          onMouseDown={(e) => e.preventDefault()}
          data-testid={`${testId}-popover`}
        >
          <div className="flex items-end gap-3 border-b border-[var(--pf-border)] pb-2">
            <div>
              <p className="font-heading text-3xl font-bold" style={{ color }}>{data.score}</p>
              <p className="text-[9px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)] -mt-1">Library Health</p>
            </div>
            <div className="flex-1 text-right">
              <span className={`text-xs px-2 py-0.5 font-bold uppercase tracking-wider ${gradeBg}`}>Grade {data.grade}</span>
              <p className="text-[10px] text-[var(--pf-text-muted)] mt-1">{data.total_blocks} blocks total</p>
            </div>
          </div>

          <div className="space-y-1.5">
            {[
              { label: "Strength", pct: data.breakdown.strength, meta: `${data.metrics.strong} strong · ${data.metrics.weak} weak`, weight: 40 },
              { label: "Breadth", pct: data.breakdown.breadth, meta: `${data.metrics.covered_cells}/${data.metrics.total_cells} cells covered`, weight: 30 },
              { label: "Recency", pct: data.breakdown.recency, meta: `${data.metrics.recent_30d} added in 30d`, weight: 30 },
            ].map((row) => (
              <div key={row.label} className="flex items-center gap-2 text-[10px]">
                <span className="text-[var(--pf-text-muted)] uppercase tracking-[0.15em] font-semibold w-16 shrink-0">{row.label}</span>
                <div className="flex-1 h-1.5 bg-[var(--pf-surface)] relative">
                  <div className="absolute left-0 top-0 h-full bg-[var(--pf-accent)]" style={{ width: `${row.pct}%` }} />
                </div>
                <span className="font-mono font-semibold text-[var(--pf-text)] w-8 text-right">{row.pct}</span>
                <span className="text-[var(--pf-text-muted)] w-6 text-right">·{row.weight}%</span>
              </div>
            ))}
            <div className="flex items-center gap-2 text-[10px] pt-1">
              <span className="text-[var(--pf-text-muted)] uppercase tracking-[0.15em] font-semibold w-16 shrink-0"></span>
              <span className="flex-1 text-[var(--pf-text-muted)]">{data.breakdown.strength} × 40% + {data.breakdown.breadth} × 30% + {data.breakdown.recency} × 30%</span>
            </div>
          </div>

          {data.suggestions?.length > 0 && (
            <div className="border-t border-[var(--pf-border)] pt-2 space-y-1">
              <p className="text-[9px] uppercase tracking-[0.2em] font-semibold text-[var(--pf-text-muted)] mb-1">Nudges</p>
              {data.suggestions.slice(0, 3).map((s, i) => (
                <p key={i} className="text-[10px] text-[var(--pf-text-secondary)] leading-relaxed">→ {s}</p>
              ))}
            </div>
          )}

          <Link to="/library" onClick={() => setOpen(false)} data-testid={`${testId}-cta`}
            className="block w-full text-center text-[11px] font-medium bg-[var(--pf-accent)] hover:bg-[var(--pf-accent-hover)] text-white py-1.5">
            Open Library →
          </Link>
        </div>
      )}
    </div>
  );
}
